import * as Sentry from "@sentry/react";

import {
  createWorkspaceChannel,
  type WorkspaceChannel,
  type WorkspaceMessage,
} from "./workspace.channel";
import {
  canonicalWorkspaceDocument,
  createBlankWorkspaceDocument,
  createDefaultWorkspaceDocument,
  createInitialWorkspaceDocument,
  createWorkspaceEntry,
  duplicateWorkspaceEntry,
  instantiateWorkspaceEntry,
  type WorkspaceDocument,
  type WorkspaceEntry,
  withWorkspaceGroup,
  workspaceDocumentsEqual,
} from "./workspace.document";
import { createWorkspaceLock, type WorkspaceLock } from "./workspace.lock";
import {
  clearPendingWorkspaceSnapshot,
  getPendingWorkspaceMutationStatus,
  type PendingWorkspaceMutationStatus,
  type PendingWorkspaceSnapshot,
  readPendingWorkspaceSnapshots,
  readWorkspaceDocument,
  WORKSPACE_PENDING_KEY_PREFIX,
  WORKSPACE_STORAGE_KEY,
  type WorkspaceStorage,
  writePendingWorkspaceSnapshot,
  writeWorkspaceDocument,
} from "./workspace.storage";
import type { GroupId, WorkspaceSnapshot } from "./workspace.types";
import {
  resolveWorkspaceTemplateSettings,
  WORKSPACE_TEMPLATES,
  type WorkspaceTemplateId,
} from "./workspaceTemplates";

type WorkspaceMutation = (
  document: WorkspaceDocument,
) => WorkspaceDocument | undefined;

export class WorkspaceRepositoryWriteError extends Error {
  constructor() {
    super("Unable to save workspace changes");
    this.name = "WorkspaceRepositoryWriteError";
  }
}

export class WorkspaceRepositoryDisposedError extends Error {
  constructor() {
    super("The workspace repository has been disposed");
    this.name = "WorkspaceRepositoryDisposedError";
  }
}

const reportWorkspaceRepositoryError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: { component: "workspace-repository" },
  });
};

export interface WorkspaceCommitResult {
  document: WorkspaceDocument;
  didWrite: boolean;
  consumedMutationIds: string[];
}

export type CloseWorkspaceResult =
  | { status: "closed" }
  | { status: "not-found" }
  | { status: "last-workspace" };

export type DuplicateWorkspaceResult =
  | { status: "duplicated"; entry: WorkspaceEntry }
  | { status: "not-found" };

export type RenameWorkspaceResult =
  | { status: "renamed" }
  | { status: "not-found" }
  | { status: "unchanged" };

export interface WorkspaceRepository {
  getSnapshot: () => WorkspaceDocument;
  subscribe: (listener: () => void) => () => void;
  createWorkspace: (
    templateId?: WorkspaceTemplateId,
    options?: { groupId?: GroupId },
  ) => Promise<WorkspaceEntry>;
  duplicateWorkspace: (
    sourceWorkspaceId: string,
  ) => Promise<DuplicateWorkspaceResult>;
  renameWorkspace: (
    workspaceId: string,
    name: string,
  ) => Promise<RenameWorkspaceResult>;
  reorderWorkspace: (
    sourceWorkspaceId: string,
    targetWorkspaceId: string,
  ) => Promise<WorkspaceCommitResult>;
  closeWorkspace: (workspaceId: string) => Promise<CloseWorkspaceResult>;
  reset: (kind: "defaults" | "blank") => Promise<WorkspaceCommitResult>;
  mutateWorkspace: (
    workspaceId: string,
    mutation: (entry: {
      workspace: WorkspaceSnapshot;
      settings: Record<string, unknown>;
    }) => {
      workspace: WorkspaceSnapshot;
      settings: Record<string, unknown>;
    },
  ) => Promise<WorkspaceCommitResult>;
  stageWorkspaceSnapshot: (
    workspaceId: string,
    workspace: WorkspaceSnapshot,
    settings: Record<string, unknown>,
  ) => PendingWorkspaceSnapshot | undefined;
  getPendingWorkspaceMutationStatus: (
    workspaceId: string,
    mutationId: string,
  ) => PendingWorkspaceMutationStatus;
  recoverPendingWorkspaces: () => Promise<WorkspaceCommitResult>;
  flush: () => Promise<void>;
  dispose: () => void;
  isDisposed: () => boolean;
}

export interface CreateWorkspaceRepositoryOptions {
  channel?: WorkspaceChannel;
  createChannel?: () => WorkspaceChannel;
  writerId?: string;
  storage?: WorkspaceStorage;
  lock?: WorkspaceLock;
}

const selectWorkspaceWinner = (
  first: WorkspaceDocument,
  ...candidates: Array<WorkspaceDocument | undefined>
): WorkspaceDocument =>
  candidates.reduce<WorkspaceDocument>((winner, candidate) => {
    if (!candidate || candidate.revision < winner.revision) {
      return winner;
    }
    if (candidate.revision > winner.revision) {
      return candidate;
    }
    return workspaceDocumentsEqual(candidate, winner)
      ? winner
      : canonicalWorkspaceDocument(candidate, winner);
  }, first);

export const createWorkspaceRepository = (
  options: CreateWorkspaceRepositoryOptions = {},
): WorkspaceRepository => {
  const storage = options.storage ?? window.localStorage;
  const workspaceLock = options.lock ?? createWorkspaceLock();
  const ownsChannel = options.channel === undefined;
  const channel =
    options.channel ?? options.createChannel?.() ?? createWorkspaceChannel();
  const writerId = options.writerId ?? crypto.randomUUID();
  const listeners = new Set<() => void>();
  let disposed = false;
  let lifecycleGeneration = 0;
  let current =
    readWorkspaceDocument(storage) ?? createInitialWorkspaceDocument();
  let pending: Promise<unknown> = Promise.resolve();
  let pendingRecovery: Promise<WorkspaceCommitResult> | undefined;
  let recoveryGeneration = 0;

  const isActive = (generation: number) =>
    !disposed && lifecycleGeneration === generation;

  const assertActive = (generation: number) => {
    if (!isActive(generation)) {
      throw new WorkspaceRepositoryDisposedError();
    }
  };

  const notify = (generation: number) => {
    for (const listener of [...listeners]) {
      assertActive(generation);
      listener();
    }
  };

  const adopt = (document: WorkspaceDocument, generation: number) => {
    assertActive(generation);
    if (workspaceDocumentsEqual(current, document)) {
      return;
    }
    assertActive(generation);
    current = structuredClone(document);
    notify(generation);
  };

  const enqueue = <T>(operation: () => Promise<T>): Promise<T> => {
    const run = pending.then(operation, operation);
    pending = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  };

  const persistAndAnnounce = (
    document: WorkspaceDocument,
    generation: number,
  ) => {
    assertActive(generation);
    if (
      !writeWorkspaceDocument(document, storage, () => assertActive(generation))
    ) {
      throw new WorkspaceRepositoryWriteError();
    }
    assertActive(generation);
    adopt(document, generation);
    assertActive(generation);
    channel.post({ writerId, document });
  };

  const mergePendingSnapshots = (document: WorkspaceDocument) => {
    const snapshots = readPendingWorkspaceSnapshots(storage);
    let changed = false;
    const workspaces = document.workspaces.map((entry) => {
      const pendingSnapshot = snapshots.find(
        (snapshot) => snapshot.workspaceId === entry.id,
      );
      if (!pendingSnapshot) {
        return entry;
      }
      if (
        JSON.stringify(entry.workspace) ===
          JSON.stringify(pendingSnapshot.workspace) &&
        JSON.stringify(entry.settings) ===
          JSON.stringify(pendingSnapshot.settings)
      ) {
        return entry;
      }
      changed = true;
      return {
        ...entry,
        workspace: structuredClone(pendingSnapshot.workspace),
        settings: structuredClone(pendingSnapshot.settings),
      };
    });
    return {
      document: changed ? { ...document, workspaces } : document,
      snapshots,
      changed,
    };
  };

  const clearPendingSnapshots = (
    snapshots: ReturnType<typeof readPendingWorkspaceSnapshots>,
    generation: number,
  ) => {
    for (const snapshot of snapshots) {
      assertActive(generation);
      try {
        clearPendingWorkspaceSnapshot(snapshot, storage, () =>
          assertActive(generation),
        );
      } catch (error) {
        if (error instanceof WorkspaceRepositoryDisposedError) {
          throw error;
        }
      }
    }
  };

  const recoverPendingLocked = (
    base: WorkspaceDocument,
    generation: number,
  ): WorkspaceCommitResult => {
    assertActive(generation);
    const recovered = mergePendingSnapshots(base);
    const consumedMutationIds = recovered.snapshots.map(
      (snapshot) => snapshot.mutationId,
    );
    if (!recovered.changed) {
      adopt(base, generation);
      clearPendingSnapshots(recovered.snapshots, generation);
      return {
        document: current,
        didWrite: false,
        consumedMutationIds,
      };
    }
    persistAndAnnounce(
      {
        ...recovered.document,
        revision: base.revision + 1,
      },
      generation,
    );
    clearPendingSnapshots(recovered.snapshots, generation);
    return {
      document: current,
      didWrite: true,
      consumedMutationIds,
    };
  };

  const seedGeneration = lifecycleGeneration;
  const seed = enqueue(() =>
    workspaceLock.run(async () => {
      if (!isActive(seedGeneration)) {
        return;
      }
      const stored = readWorkspaceDocument(storage);
      if (stored) {
        recoverPendingLocked(stored, seedGeneration);
        return;
      }
      persistAndAnnounce(current, seedGeneration);
      recoverPendingLocked(current, seedGeneration);
    }),
  );
  void seed.catch(reportWorkspaceRepositoryError);

  const reconcileRemote = (document: WorkspaceDocument) => {
    const generation = lifecycleGeneration;
    return enqueue(() =>
      workspaceLock.run(async () => {
        if (!isActive(generation)) {
          return;
        }
        const stored = readWorkspaceDocument(storage);
        const winner = selectWorkspaceWinner(document, stored, current);
        const needsRepair = !stored || !workspaceDocumentsEqual(stored, winner);
        if (needsRepair) {
          persistAndAnnounce(winner, generation);
        } else {
          adopt(winner, generation);
        }
      }),
    );
  };

  const acceptRemote = (message: WorkspaceMessage) => {
    if (disposed || message.writerId === writerId) {
      return;
    }
    const remote = message.document;
    if (remote.revision < current.revision) {
      return;
    }
    if (
      remote.revision === current.revision &&
      workspaceDocumentsEqual(remote, current)
    ) {
      return;
    }
    void reconcileRemote(remote).catch(reportWorkspaceRepositoryError);
  };

  const inactiveRecoveryResult = (): WorkspaceCommitResult => ({
    document: current,
    didWrite: false,
    consumedMutationIds: [],
  });

  const queuePendingRecovery = (): Promise<WorkspaceCommitResult> => {
    if (disposed) {
      return Promise.resolve(inactiveRecoveryResult());
    }
    const generation = lifecycleGeneration;
    recoveryGeneration += 1;
    if (pendingRecovery) {
      return pendingRecovery;
    }
    const run = enqueue(async () => {
      const consumedMutationIds = new Set<string>();
      let didWrite = false;
      while (true) {
        const passGeneration = recoveryGeneration;
        const result = await workspaceLock.run(async () => {
          if (!isActive(generation)) {
            return inactiveRecoveryResult();
          }
          const stored = readWorkspaceDocument(storage);
          const base = selectWorkspaceWinner(current, stored);
          return recoverPendingLocked(base, generation);
        });
        didWrite ||= result.didWrite;
        for (const mutationId of result.consumedMutationIds) {
          consumedMutationIds.add(mutationId);
        }
        if (passGeneration === recoveryGeneration) {
          return {
            document: current,
            didWrite,
            consumedMutationIds: [...consumedMutationIds],
          };
        }
      }
    });
    const tracked = run.finally(() => {
      if (pendingRecovery === tracked) {
        pendingRecovery = undefined;
      }
    });
    pendingRecovery = tracked;
    return tracked;
  };

  const unsubscribeChannel = channel.subscribe(acceptRemote);
  const handleStorage = (event: StorageEvent) => {
    if (disposed) {
      return;
    }
    if (event.key?.startsWith(WORKSPACE_PENDING_KEY_PREFIX)) {
      void queuePendingRecovery().catch(reportWorkspaceRepositoryError);
      return;
    }
    if (event.key !== null && event.key !== WORKSPACE_STORAGE_KEY) {
      return;
    }
    const document = readWorkspaceDocument(storage);
    if (document) {
      acceptRemote({ writerId: "", document });
    }
  };
  window.addEventListener("storage", handleStorage);

  const commit = (
    mutation: WorkspaceMutation,
  ): Promise<WorkspaceCommitResult> => {
    if (disposed) {
      return Promise.reject(new WorkspaceRepositoryDisposedError());
    }
    const generation = lifecycleGeneration;
    return enqueue(() => {
      assertActive(generation);
      return workspaceLock.run(async () => {
        assertActive(generation);
        const stored = readWorkspaceDocument(storage);
        const base = selectWorkspaceWinner(current, stored);
        const recovered = mergePendingSnapshots(base);
        const mutated = mutation(structuredClone(recovered.document));
        if (!mutated && !recovered.changed) {
          adopt(base, generation);
          clearPendingSnapshots(recovered.snapshots, generation);
          return {
            document: current,
            didWrite: false,
            consumedMutationIds: recovered.snapshots.map(
              (snapshot) => snapshot.mutationId,
            ),
          };
        }
        persistAndAnnounce(
          {
            ...(mutated ?? recovered.document),
            schemaVersion: 3,
            revision: base.revision + 1,
            engine: "rgl",
          },
          generation,
        );
        clearPendingSnapshots(recovered.snapshots, generation);
        return {
          document: current,
          didWrite: true,
          consumedMutationIds: recovered.snapshots.map(
            (snapshot) => snapshot.mutationId,
          ),
        };
      });
    });
  };

  const closeWorkspace = async (
    workspaceId: string,
  ): Promise<CloseWorkspaceResult> => {
    let status: CloseWorkspaceResult["status"] = "not-found";
    await commit((document) => {
      if (!document.workspaces.some((entry) => entry.id === workspaceId)) {
        status = "not-found";
        return undefined;
      }
      if (document.workspaces.length <= 1) {
        status = "last-workspace";
        return undefined;
      }
      status = "closed";
      return {
        ...document,
        workspaces: document.workspaces.filter(
          (entry) => entry.id !== workspaceId,
        ),
      };
    });
    return { status };
  };

  return {
    getSnapshot: () => current,
    subscribe: (listener) => {
      if (disposed) {
        return () => {};
      }
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    createWorkspace: async (templateId?, options?) => {
      let created: WorkspaceEntry | undefined;
      await commit((document) => {
        const names = new Set(document.workspaces.map((entry) => entry.name));
        const baseName = templateId
          ? WORKSPACE_TEMPLATES[templateId].label
          : "Workspace";
        let name = baseName;
        let suffix = 2;
        while (names.has(name)) {
          name = `${baseName} ${suffix++}`;
        }
        if (templateId) {
          const template = WORKSPACE_TEMPLATES[templateId];
          const groupId = options?.groupId;
          created = instantiateWorkspaceEntry(
            name,
            groupId
              ? withWorkspaceGroup(template.workspace, groupId)
              : template.workspace,
            resolveWorkspaceTemplateSettings(templateId),
            templateId,
          );
        } else {
          created = createWorkspaceEntry(name);
        }
        return {
          ...document,
          workspaces: [...document.workspaces, created],
        };
      });
      if (!created) {
        throw new Error("Workspace was not created");
      }
      return created;
    },
    duplicateWorkspace: async (sourceWorkspaceId) => {
      let duplicate: WorkspaceEntry | undefined;
      let status: DuplicateWorkspaceResult["status"] = "not-found";
      await commit((document) => {
        const sourceIndex = document.workspaces.findIndex(
          (entry) => entry.id === sourceWorkspaceId,
        );
        if (sourceIndex < 0) {
          status = "not-found";
          return undefined;
        }
        const source = document.workspaces[sourceIndex];
        const names = new Set(document.workspaces.map((entry) => entry.name));
        const copyName = (copyNumber?: number) => {
          const suffix =
            copyNumber === undefined ? " copy" : ` copy ${copyNumber}`;
          const stem = source.name.slice(0, 100 - suffix.length).trimEnd();
          return `${stem}${suffix}`;
        };
        let name = copyName();
        let suffix = 2;
        while (names.has(name)) {
          name = copyName(suffix++);
        }
        duplicate = duplicateWorkspaceEntry(source, name);
        status = "duplicated";
        const workspaces = [...document.workspaces];
        workspaces.splice(sourceIndex + 1, 0, duplicate);
        return { ...document, workspaces };
      });
      if (status === "not-found" || !duplicate) {
        return { status: "not-found" };
      }
      return { status: "duplicated", entry: duplicate };
    },
    renameWorkspace: async (workspaceId, name) => {
      const trimmed = name.trim().slice(0, 100);
      let status: RenameWorkspaceResult["status"] = "unchanged";
      await commit((document) => {
        const entry = document.workspaces.find(
          (item) => item.id === workspaceId,
        );
        if (!entry) {
          status = "not-found";
          return undefined;
        }
        if (!trimmed || entry.name === trimmed) {
          status = "unchanged";
          return undefined;
        }
        status = "renamed";
        return {
          ...document,
          workspaces: document.workspaces.map((item) =>
            item.id === workspaceId ? { ...item, name: trimmed } : item,
          ),
        };
      });
      return { status };
    },
    reorderWorkspace: (sourceWorkspaceId, targetWorkspaceId) =>
      commit((document) => {
        const source = document.workspaces.findIndex(
          (entry) => entry.id === sourceWorkspaceId,
        );
        const target = document.workspaces.findIndex(
          (entry) => entry.id === targetWorkspaceId,
        );
        if (source < 0 || target < 0 || source === target) {
          return undefined;
        }
        const workspaces = [...document.workspaces];
        const [moved] = workspaces.splice(source, 1);
        workspaces.splice(target, 0, moved);
        return { ...document, workspaces };
      }),
    closeWorkspace,
    reset: (kind) =>
      commit((document) => {
        const reset =
          kind === "blank"
            ? createBlankWorkspaceDocument()
            : createDefaultWorkspaceDocument();
        return { ...document, workspaces: reset.workspaces };
      }),
    mutateWorkspace: (workspaceId, mutation) =>
      commit((document) => {
        const entry = document.workspaces.find(
          (item) => item.id === workspaceId,
        );
        if (!entry) {
          return undefined;
        }
        const next = mutation({
          workspace: structuredClone(entry.workspace),
          settings: structuredClone(entry.settings),
        });
        if (
          JSON.stringify(next.workspace) === JSON.stringify(entry.workspace) &&
          JSON.stringify(next.settings) === JSON.stringify(entry.settings)
        ) {
          return undefined;
        }
        return {
          ...document,
          workspaces: document.workspaces.map((item) =>
            item.id === workspaceId
              ? { ...item, ...structuredClone(next) }
              : item,
          ),
        };
      }),
    stageWorkspaceSnapshot: (workspaceId, workspace, settings) => {
      const generation = lifecycleGeneration;
      assertActive(generation);
      return writePendingWorkspaceSnapshot(
        { workspaceId, writerId, workspace, settings },
        storage,
        () => assertActive(generation),
      );
    },
    getPendingWorkspaceMutationStatus: (workspaceId, mutationId) =>
      getPendingWorkspaceMutationStatus(workspaceId, mutationId, storage),
    recoverPendingWorkspaces: () => {
      if (disposed) {
        return Promise.reject(new WorkspaceRepositoryDisposedError());
      }
      const generation = lifecycleGeneration;
      return queuePendingRecovery().then((result) => {
        assertActive(generation);
        return result;
      });
    },
    flush: async () => {
      await pending;
    },
    dispose: () => {
      if (disposed) {
        return;
      }
      disposed = true;
      lifecycleGeneration += 1;
      listeners.clear();
      unsubscribeChannel();
      window.removeEventListener("storage", handleStorage);
      if (ownsChannel) {
        channel.close();
      }
    },
    isDisposed: () => disposed,
  };
};
